import { useEffect, useMemo, useState } from 'react';
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';

import { Button } from '../../components/shared/FormElements';
import { ProcessRoleType } from '../../types';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { setLoading, setPopAlert } from '../../slices/miscSlice';
import { setProcessRoles } from '../../slices/permissionsSlice';
import { useAppContext } from '../../Context';
import { constants } from '../../constants';
import '../../styles/DataTable.css';
import { usePermissions } from '../../services';

const ViewProcessRoles: React.FC = () => {
  const [addNewForm, setAddNewForm] = useState(false);
  const [editForm, setEditForm] = useState(false);
  const [newRoleName, setNewRoleName] = useState('' as string);
  const [editRole, setEditRole] = useState({} as ProcessRoleType);
  const [selectedPermissions, setSelectedPermissions] = useState<Array<string>>([]);
  const [permissionOptions, setPermissionOptions] = useState<Array<{ permission: string; label: string }>>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const { process_roles } = useAppSelector((state) => state.permissions);
  const dispatch = useAppDispatch();
  const { apiCall } = useAppContext();
  const { canView_processrole, canAdd_processrole, canChange_processrole, canDelete_processrole } = usePermissions([
    'view_processrole',
    'add_processrole',
    'change_processrole',
    'delete_processrole',
  ]);

  useEffect(() => {
    if (!canView_processrole) return;
    if (permissionsLoaded) return;
    const normalizeCode = (code: string) => code.trim();
    const resolveLabel = (code: string, name?: string) =>
      constants.permission_labels[code] ||
      constants.permission_labels[code.toLowerCase()] ||
      name ||
      code;
    const loadPermissions = async () => {
      const response = await apiCall('GET', 'permissions/catalog/', null, '', 'Error al obtener los permisos');
      if (response?.process_permissions) {
        const options = response.process_permissions
          .map((permission: { permission?: string; label?: string }) => {
            const code = normalizeCode(permission.permission || '');
            if (!code) return null;
            const label = resolveLabel(code, permission.label);
            return { permission: code, label };
          })
          .filter(Boolean) as Array<{ permission: string; label: string }>;
        setPermissionOptions(options.sort((a, b) => a.label.localeCompare(b.label)));
        setPermissionsLoaded(true);
        return;
      }

      const fallback = Object.values(constants.process_permissions)
        .map((permission) => ({
          permission,
          label: constants.permission_labels[permission] || permission,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setPermissionOptions(fallback);
      setPermissionsLoaded(true);
    };

    loadPermissions();
  }, [apiCall, permissionsLoaded, canView_processrole]);

  const permissionGroups = useMemo(() => {
    const groups: Record<string, Array<{ permission: string; label: string }>> = {};
    const excludedPrefixes = new Set(['session', 'contenttype', 'token', 'tokenproxy']);
    const excludedPermissions = new Set([
      'add_alegraintegration',
      'add_category',
      'add_contenttype',
      'add_designersprobabilities',
      'add_list',
      'add_listitem',
      'add_logentry',
      'add_measurement',
      'add_pqrsticket',
      'add_pqrsticketlogger',
      'add_processrolepermission',
      'add_processroleuser',
      'add_repositionevidence',
      'add_repositionlog',
      'add_session',
      'add_siigointegration',
      'add_sketchrequestlogger',
      'add_stockpermission',
      'add_stocktransaction',
      'add_stocktransactionorder',
      'add_task',
      'add_token',
      'add_tokenproxy',
      'add_useraudit',
      'add_userlog',
      'add_vendor',
      'change_alegraintegration',
      'change_category',
      'change_contenttype',
      'change_groupotline',
      'change_list',
      'change_listitem',
      'change_logentry',
      'change_measurement',
      'change_pqrsticket',
      'change_pqrsticketlogger',
      'change_processrolepermission',
      'change_processroleuser',
      'change_repositionevidence',
      'change_repositionlog',
      'change_session',
      'change_siigointegration',
      'change_sketchrequestlogger',
      'change_stockpermission',
      'change_stocktransaction',
      'change_stocktransactionorder',
      'change_task',
      'change_token',
      'change_tokenproxy',
      'change_useraudit',
      'change_userlog',
      'change_vendor',
      'delete_alegraintegration',
      'delete_category',
      'delete_company',
      'delete_contenttype',
      'delete_designersprobabilities',
      'delete_list',
      'delete_listitem',
      'delete_logentry',
      'delete_measurement',
      'delete_pqrsticket',
      'delete_pqrsticketlogger',
      'delete_processrolepermission',
      'delete_processroleuser',
      'delete_repositionevidence',
      'delete_repositionlog',
      'delete_session',
      'delete_siigointegration',
      'delete_stockpermission',
      'delete_stocktransaction',
      'delete_stocktransactionorder',
      'delete_task',
      'delete_token',
      'delete_tokenproxy',
      'delete_useraudit',
      'delete_userlog',
      'delete_vendor',
      'view_alegraintegration',
      'view_category',
      'view_contenttype',
      'view_list',
      'view_listitem',
      'view_logentry',
      'view_measurement',
      'view_pqrsticket',
      'view_pqrsticketlogger',
      'view_processrolepermission',
      'view_processroleuser',
      'view_repositionevidence',
      'view_repositionlog',
      'view_session',
      'view_siigointegration',
      'view_sketchrequestlogger',
      'view_stockpermission',
      'view_stocktransaction',
      'view_stocktransactionorder',
      'view_task',
      'view_token',
      'view_tokenproxy',
      'view_userlog',
      'view_vendor',
    ]);
    const sectionLabelMap: Record<string, string> = {
      orders: 'Ordenes',
      orderprocess: 'Procesos de Ordenes',
      orderprocesslog: 'Log de Procesos',
      orderprocessconsecutive: 'Procesos de Consecutivos',
      orderprocessconsecutivelog: 'Log de Procesos de Consecutivos',
      transactions: 'Transacciones',
      sketches: 'Bocetos',
      sketchrequests: 'Solicitudes de Bocetos',
      production: 'Produccion',
      processes: 'Procesos',
      'production processes': 'Procesos de Produccion',
      'production_processes': 'Procesos de Produccion',
      repositions: 'Reposiciones',
    };
    const groupLabelFor = (permission: string, label: string) => {
      if (permission.startsWith('view_menu_')) {
        return 'Menú';
      }
      const cleaned = label.replace(/^(Ver|Crear|Editar|Eliminar)\s+/i, '').trim();
      if (cleaned && cleaned !== label) {
        const mapped = sectionLabelMap[cleaned.toLowerCase()];
        return mapped || cleaned;
      }
      const parts = permission.split('_').slice(1).join(' ');
      if (!parts) return permission;
      const mapped = sectionLabelMap[parts.toLowerCase()] || sectionLabelMap[parts.toLowerCase().replace(/\s+/g, '_')];
      return mapped || parts;
    };
    permissionOptions.forEach((option) => {
      if (excludedPermissions.has(option.permission)) return;
      const domain = option.permission.split('_')[1];
      if (domain && excludedPrefixes.has(domain)) return;
      const groupLabel = groupLabelFor(option.permission, option.label);
      if (!groups[groupLabel]) {
        groups[groupLabel] = [];
      }
      groups[groupLabel].push(option);
    });
    return Object.entries(groups)
      .map(([groupLabel, items]) => ({
        groupLabel,
        items: items.sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .sort((a, b) => a.groupLabel.localeCompare(b.groupLabel));
  }, [permissionOptions]);

  const handleTogglePermission = (permission: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permission) ? prev.filter((item) => item !== permission) : [...prev, permission],
    );
  };

  const handleSaveRole = async () => {
    if (newRoleName === '') {
      dispatch(
        setPopAlert({
          alertType: 'error',
          message: 'El nombre del rol de proceso es requerido',
          show: true,
        }),
      );
      return;
    }
    dispatch(setLoading(true));
    const data = {
      name: newRoleName,
      permissions: selectedPermissions,
    };
    const response = await apiCall(
      'POST',
      'process_roles/',
      data,
      'Rol de proceso creado exitosamente',
      'Error al crear el rol de proceso',
    );
    dispatch(setLoading(false));
    if (response?.id) {
      dispatch(setProcessRoles([...process_roles, response]));
      setAddNewForm(false);
      setNewRoleName('');
      setSelectedPermissions([]);
    } else {
      dispatch(
        setPopAlert({
          alertType: 'error',
          message: 'Error al crear el rol de proceso',
          show: true,
        }),
      );
    }
  };

  const handleEditRole = (role: ProcessRoleType) => {
    setEditForm(true);
    setAddNewForm(false);
    setEditRole(role);
    setSelectedPermissions(role.permissions || []);
  };

  const handleUpdateRole = async () => {
    if (editRole.name === '') {
      dispatch(
        setPopAlert({
          alertType: 'error',
          message: 'El nombre del rol de proceso es requerido',
          show: true,
        }),
      );
      return;
    }
    dispatch(setLoading(true));
    const data = {
      name: editRole.name,
      permissions: selectedPermissions,
    };
    const response = await apiCall(
      'PUT',
      `process_roles/${editRole.id}/`,
      data,
      'Rol de proceso actualizado exitosamente',
      'Error al actualizar el rol de proceso',
    );
    dispatch(setLoading(false));
    if (response?.id) {
      const updatedRoles = process_roles.map((role: ProcessRoleType) => (role.id === response.id ? response : role));
      dispatch(setProcessRoles(updatedRoles));
      setEditForm(false);
      setEditRole({} as ProcessRoleType);
      setSelectedPermissions([]);
    } else {
      dispatch(
        setPopAlert({
          alertType: 'error',
          message: 'Error al actualizar el rol de proceso',
          show: true,
        }),
      );
    }
  };

  const handleDeleteRole = async (id: number) => {
    dispatch(setLoading(true));
    const response = await apiCall(
      'DELETE',
      `process_roles/${id}/`,
      null,
      'Rol de proceso eliminado exitosamente',
      'Error al eliminar el rol de proceso',
    );
    dispatch(setLoading(false));
    if (response) {
      const updatedRoles = process_roles.filter((role: ProcessRoleType) => role.id !== id);
      dispatch(setProcessRoles(updatedRoles));
    }
  };

  const renderPermissionsForm = () => (
    <FormControl component="fieldset" variant="standard">
      <FormGroup>
        <div style={{ maxHeight: '240px', overflowY: 'auto', paddingRight: '0.5rem' }}>
          {permissionGroups.map((group) => (
            <div key={group.groupLabel} style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{group.groupLabel}</div>
              {group.items.map((option) => (
                <FormControlLabel
                  key={option.permission}
                  control={
                    <Switch
                      checked={selectedPermissions.includes(option.permission)}
                      onChange={() => handleTogglePermission(option.permission)}
                      name={option.permission}
                    />
                  }
                  label={option.label}
                />
              ))}
            </div>
          ))}
        </div>
      </FormGroup>
    </FormControl>
  );

  if (!canView_processrole) {
    return null;
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10} className="text-right">
          <h2>Roles de Procesos</h2>
        </Grid>
        <Grid item xs={12} sm={2} className="text-right">
          {canAdd_processrole && (
            <Button
              onClick={() => {
                setAddNewForm(true);
                setEditForm(false);
                setSelectedPermissions([]);
              }}
            >
              Agregar
            </Button>
          )}
        </Grid>
      </Grid>
      {addNewForm && canAdd_processrole && (
        <div>
          <h3>Agregar Nuevo Rol de Proceso</h3>
          <form>
            <input
              type="text"
              placeholder="Nombre del rol de proceso"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              className="settings-input form-control"
            />
            <div>
              <h4>Permisos</h4>
              {renderPermissionsForm()}
            </div>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  onClick={() => {
                    setAddNewForm(false);
                    setSelectedPermissions([]);
                    setNewRoleName('');
                  }}
                >
                  Cancelar
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button onClick={handleSaveRole}>Guardar</Button>
              </Grid>
            </Grid>
          </form>
        </div>
      )}
      {editForm && canChange_processrole && (
        <div>
          <h3>Editar Rol de Proceso</h3>
          <form>
            <input
              type="text"
              placeholder="Nombre del rol de proceso"
              value={editRole.name || ''}
              onChange={(e) => setEditRole({ ...editRole, name: e.target.value })}
              className="settings-input form-control"
            />
            <div>
              <h4>Permisos</h4>
              {renderPermissionsForm()}
            </div>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  onClick={() => {
                    setEditForm(false);
                    setEditRole({} as ProcessRoleType);
                    setSelectedPermissions([]);
                  }}
                >
                  Cancelar
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button onClick={handleUpdateRole}>Guardar Cambios</Button>
              </Grid>
            </Grid>
          </form>
        </div>
      )}
      {process_roles?.length === 0 && <p>No hay roles de procesos registrados</p>}
      <Grid container spacing={2}>
        <div className="table-responsive">
          <TableContainer className="table-container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>NOMBRE</b>
                  </TableCell>
                  <TableCell width={150}>
                    <b>ACCIONES</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {process_roles?.map((role: ProcessRoleType) => (
                  <TableRow key={role.id}>
                    <TableCell>{role.name}</TableCell>
                    <TableCell>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          {canChange_processrole && (
                            <Button onClick={() => handleEditRole(role)}>
                              <FontAwesomeIcon icon={faPencil} />
                            </Button>
                          )}
                        </Grid>
                        <Grid item xs={6}>
                          {canDelete_processrole && (
                            <Button onClick={() => handleDeleteRole(role.id)}>
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Grid>
    </>
  );
};

export default ViewProcessRoles;
